import { isJwtUsable } from '~/services/jwt/validation';
import { CustomStorage, LocalStorage } from '~/services/storage';
import { retryOnce } from '~/shared/utils/retry';
import { ALTAPAY_TOKEN_RENEWAL_BUFFER_IN_MINUTES, AltaPayStorageKey } from './constants';
import { RequestFailure } from './lib/errors';

const AUTHENTICATION_PATH = '/api/altapay/authentication';

class AltaPayAuth {
    private static instance: AltaPayAuth;

    private _storage: CustomStorage;
    private _requests: Map<string | undefined, Promise<string>>;

    static getInstance() {
        if (!AltaPayAuth.instance) {
            AltaPayAuth.instance = new AltaPayAuth();
        }

        return AltaPayAuth.instance;
    }

    private constructor() {
        this._storage = new LocalStorage();
        this._requests = new Map();

        this.reauthenticate = this.reauthenticate.bind(this);
    }

    private _getStoredToken(): string | null {
        const token = this._storage.get(AltaPayStorageKey.AUTH);

        return typeof token === 'string' ? token : null;
    }

    private async _fetchToken(sessionId?: string): Promise<string> {
        const response = await fetch(AUTHENTICATION_PATH, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
        });

        const isJSON = !!response.headers.get('Content-Type')?.includes('application/json');
        const data = isJSON ? await response.json().catch(() => undefined) : undefined;

        if (!response.ok || typeof data?.token !== 'string') {
            const failure: RequestFailure = {
                errorMessage: isJSON
                    ? (data?.errorMessage ?? 'No token in response.')
                    : 'Response is not JSON.',
                status: response.status,
                path: AUTHENTICATION_PATH,
            };

            throw failure;
        }

        return data.token;
    }

    // We will retry after a brief moment. This covers the most
    // common cause of failures: short network or edge errors.
    authenticate(sessionId?: string): Promise<string> {
        return retryOnce(() => this._fetchToken(sessionId));
    }

    reauthenticate(sessionId?: string): Promise<string> {
        // Calls that need a new token at the same time will share one request.
        const pendingRequest = this._requests.get(sessionId);

        if (pendingRequest) return pendingRequest;

        const request = this.authenticate(sessionId)
            .then((token) => {
                // Only unbound tokens are stored. They are the ones used to create new sessions.
                if (!sessionId) this._storage.set(AltaPayStorageKey.AUTH, token);

                return token;
            })
            .finally(() => {
                this._requests.delete(sessionId);
            });

        this._requests.set(sessionId, request);

        return request;
    }

    async getValidToken(): Promise<string> {
        const storedToken = this._getStoredToken();

        if (isJwtUsable(storedToken, ALTAPAY_TOKEN_RENEWAL_BUFFER_IN_MINUTES)) return storedToken;

        return this.reauthenticate();
    }
}

export default AltaPayAuth.getInstance();
